// Routes distributor users (GST, SET, Toyota Canada) straight to their own IdP
// by forwarding ?domain_hint= to Entra, skipping Microsoft's email prompt.
//
// Stored in localStorage because sign-in happens several navigations after the
// landing URL, and the realm belongs to the workstation, not the session.
// resetLogoutBrowserState re-seeds this key after its wipe. A fresh inbound
// hint always overwrites it; a provider refusal clears it
// (useOidcCallbackCompletion), since a wrong realm is the likeliest cause.

import AppSettings from "@/appsettings";
import { AUTH_QUERY_KEYS, AUTH_SESSION_KEYS } from "@/constants/authFlowKeys";
import { authLocalStorage } from "@services/auth/authStorage";

const STORAGE_KEY = AUTH_SESSION_KEYS.DOMAIN_HINT;

// Allowlisted so a crafted link cannot aim sign-in at an arbitrary IdP.
const isAllowedDomainHint = (value) => {
  return (AppSettings.auth?.entra?.allowedDomainHints || []).includes(value);
};

export const getDomainHint = () => {
  const stored = authLocalStorage.get(STORAGE_KEY);
  // Re-checked on read so a value stored under an older allowlist expires.
  return stored && isAllowedDomainHint(stored) ? stored : null;
};

export const clearDomainHint = () => {
  authLocalStorage.remove(STORAGE_KEY);
};

// Reads ?domain_hint= off the landing URL at boot and returns the hint in
// effect. An absent or unrecognized param leaves the stored realm untouched.
export const captureDomainHint = (search = globalThis.location.search) => {
  let value = null;

  try {
    value = new URLSearchParams(search).get(AUTH_QUERY_KEYS.DOMAIN_HINT);
  } catch {
    return getDomainHint();
  }

  if (!value) {
    return getDomainHint();
  }

  if (!isAllowedDomainHint(value)) {
    console.warn("Ignoring unrecognized domain_hint.", value);
    return getDomainHint();
  }

  authLocalStorage.set(STORAGE_KEY, value);
  return value;
};

// Drops ?domain_hint= from the address bar once captured, so it isn't saved as
// the desired nav and replayed after login. replaceState adds no history entry.
export const stripDomainHintFromUrl = () => {
  try {
    const url = new URL(globalThis.location.href);
    if (!url.searchParams.has(AUTH_QUERY_KEYS.DOMAIN_HINT)) {
      return;
    }

    url.searchParams.delete(AUTH_QUERY_KEYS.DOMAIN_HINT);
    globalThis.history.replaceState(
      globalThis.history.state,
      "",
      `${url.pathname}${url.search}${url.hash}`,
    );
  } catch {
    // URL or history unavailable — leave the address bar as it is.
  }
};
